import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { getRefFromUrl, captureRefOnVisit, setStoredPartnerToken } from '../utils/attribution';
import { clearQualificationResponses, initQualificationResponses } from '../utils/qualificationResponses';
import LanguageSwitch from '../components/LanguageSwitch';
import AppHeader from '../components/AppHeader';
import './Landing.css';

const DEFAULT_PRICE = 250;
const PARTNER_PRICE_KEY = 'mvp_partner_price';

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [promoData, setPromoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);
  const fetchedRef = useRef(null);

  const refParam = getRefFromUrl();
  const token = refParam != null ? String(refParam).trim() : '';
  const hasRef = token !== '';

  useEffect(() => {
    captureRefOnVisit();
  }, []);

  useEffect(() => {
    if (!hasRef || !token) {
      setPromoData(null);
      return;
    }
    if (fetchedRef.current === token) return;
    setLoading(true);
    fetchedRef.current = token;
    mvpApi
      .getPartnerByToken(token)
      .then((res) => {
        const data = res.data;
        setPromoData({
          price: data.price != null ? Number(data.price) : null,
          partner_name: data.partner_name || null,
        });
        if (data.price != null) {
          try {
            sessionStorage.setItem(PARTNER_PRICE_KEY, String(data.price));
          } catch {}
        }
      })
      .catch(() => {
        setPromoData(null);
        fetchedRef.current = null;
      })
      .finally(() => setLoading(false));
  }, [hasRef, token]);

  const hasPromo = promoData && (promoData.price != null || promoData.partner_name);
  const displayPrice = hasPromo && promoData.price != null ? promoData.price : DEFAULT_PRICE;
  const showStrikethrough = hasPromo && promoData.price != null && promoData.price < DEFAULT_PRICE;

  const handleStart = () => {
    if (token) setStoredPartnerToken(token);
    clearQualificationResponses();
    initQualificationResponses();
    navigate('/es/regularizacion/qualify');
  };

  return (
    <div className="landing">
      <AppHeader rightSlot={<LanguageSwitch />} />

      <main className="landing-scroll">
        <div className="landing-inner">
          <section className="hero">
            <div className="hero-eyebrow">
              <span className="live-dot" />
              {t('landing.eyebrow')}
            </div>
            <h1 className="hero-title">
              <span className="hero-title-mobile">
                <span className="hero-title-mobile-row">
                  {t('landing.title1')}{' '}
                  <span className="hero-accent-legal">{t('landing.title2')}</span>
                </span>
                <span className="hero-title-mobile-row">
                  <span className="hero-accent-residency">{t('landing.title3')}</span>{' '}
                  {t('landing.title4')} {t('landing.titleYear')}
                </span>
              </span>
              <span className="hero-title-desktop">
                {t('landing.title1')}
                <br />
                <span className="hero-accent-legal">{t('landing.title2')}</span>
                <br className="hero-title-break-desktop" />
                <span className="hero-accent-residency">{t('landing.title3')}</span> {t('landing.title4')}
                <br className="hero-title-break-desktop" />
                {t('landing.titleYear')}
              </span>
            </h1>
            <p className="hero-body">
              {t('landing.intro1')}
            </p>
            <p className="hero-body">
              <Trans i18nKey="landing.intro2" components={{ strong: <strong /> }} />
            </p>
            <p className="hero-body">
              <strong>{t('landing.intro3')}</strong>
            </p>
            <button type="button" className="price-cta" onClick={handleStart}>
              <span>{t('landing.ctaBoxButton')}</span>
            </button>
            <p className="cta-subline">{t('landing.ctaSubline', { price: `€${displayPrice}` })}</p>
            <p className="cta-note">
              <span className="cta-note-icon" aria-hidden="true" />
              {t('landing.ctaNote')}
            </p>
          </section>

          <section className="ana-section">
            <div className="section-eyebrow section-eyebrow-accent">{t('landing.attorneySection')}</div>

            <div className="ana-media">
              <div className="ana-photo-wrap">
                <img
                  src="https://storage.readdy-site.link/project_files/bab09e94-6502-4ec6-a1a9-9ac47f321dc2/7604defd-4c0d-4532-a5bf-193596665dc7_ana-real.png?v=2bb9c6d28aefce66d34eac45aeb73e70"
                  alt="Ana Travieso"
                  className="ana-photo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop';
                  }}
                />
              </div>

              <div className="ana-creds">
                <div>
                  <div className="ana-name">Ana Travieso</div>
                  <div className="ana-title-text">{t('landing.attorneyRole')}</div>
                  <div className="ana-stats-text">{t('landing.attorneyStats')}</div>
                  <a
                    href="https://instagram.com/travieso.global"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ana-handle"
                  >
                    <i className="ri-instagram-line ana-handle-icon" aria-hidden="true" />
                    <span>{t('landing.attorneyHandle')}</span>
                  </a>
                </div>
                <div className="online-row" aria-label="Availability">
                  <span className="online-dot" aria-hidden="true" />
                  {t('landing.available')}
                </div>
              </div>

              <div className="ana-firm-badge">⚖️ {t('landing.attorneyFirmBadge')}</div>

              <div className="ana-quote">
                &quot;{t('landing.quote')}&quot;
              </div>

              <button
                type="button"
                className="bio-toggle"
                aria-expanded={bioOpen}
                onClick={() => setBioOpen((v) => !v)}
              >
                <span>{bioOpen ? t('landing.hideCreds') : t('landing.viewCreds')}</span>
                <span className={`bio-chevron ${bioOpen ? 'open' : ''}`} aria-hidden="true">
                  ▾
                </span>
              </button>

              <div className={`bio-panel ${bioOpen ? 'open' : ''}`}>
                <div className="bio-text">
                  <p>{t('landing.bioP1')}</p>
                  <p>{t('landing.bioP2')}</p>
                  <p>
                    <Trans i18nKey="landing.bioP3" components={{ strong: <strong /> }} />
                  </p>
                  <p>{t('landing.bioP4')}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="section section-with-border">
            <div className="why-heading">
              <span>{t('landing.whyTitle')}</span>
              <span className="why-heading-accent">{t('landing.whyAccent')}</span>
            </div>
            <div className="why-list">
              <div className="why-row">
                <div className="wr-icon" aria-hidden="true">
                  ✓
                </div>
                <div>
                  <div className="wr-title">{t('landing.why1Title')}</div>
                  <div className="wr-desc">
                    {t('landing.why1Desc')}
                  </div>
                </div>
              </div>

              <div className="why-row">
                <div className="wr-icon" aria-hidden="true">
                  ✓
                </div>
                <div>
                  <div className="wr-title">{t('landing.why2Title')}</div>
                  <div className="wr-desc">
                    {t('landing.why2Desc')} <strong>{t('landing.why2Strong')}</strong>
                  </div>
                </div>
              </div>

              <div className="why-row">
                <div className="wr-icon" aria-hidden="true">
                  ✓
                </div>
                <div>
                  <div className="wr-title">{t('landing.why3Title')}</div>
                  <div className="wr-desc">
                    {t('landing.why3Desc')}
                  </div>
                </div>
              </div>

              <div className="why-row">
                <div className="wr-icon" aria-hidden="true">
                  ✓
                </div>
                <div>
                  <div className="wr-title">{t('landing.why4Title')}</div>
                  <div className="wr-desc">
                    {t('landing.why4Desc1')} <strong>{t('landing.why4Desc2')}</strong> {t('landing.why4Desc3')}
                  </div>
                </div>
              </div>

              <div className="why-row">
                <div className="wr-icon" aria-hidden="true">
                  ✓
                </div>
                <div>
                  <div className="wr-title">{t('landing.why5Title')}</div>
                  <div className="wr-desc">
                    {t('landing.why5Desc')}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section section-with-border">
            <div className="how-heading">
              <span>{t('landing.howTitle')}</span>
              <span className="how-heading-accent">{t('landing.howAccent')}</span>
            </div>

            <div className="steps">
              <div className="step">
                <div className="step-spine">
                  <div className="step-num">1</div>
                  <div className="step-line" />
                </div>
                <div className="step-body">
                  <div className="step-title">{t('landing.step1Title')}</div>
                  <div className="step-desc">
                    {t('landing.step1Desc')}
                  </div>
                </div>
              </div>

              <div className="step">
                <div className="step-spine">
                  <div className="step-num">2</div>
                  <div className="step-line" />
                </div>
                <div className="step-body">
                  <div className="step-title">{t('landing.step2Title')}</div>
                  <div className="step-desc">
                    {t('landing.step2Desc')}
                  </div>
                </div>
              </div>

              <div className="step">
                <div className="step-spine">
                  <div className="step-num">3</div>
                  <div className="step-line" />
                </div>
                <div className="step-body">
                  <div className="step-title">{t('landing.step3Title')}</div>
                  <div className="step-desc">
                    <Trans i18nKey="landing.step3Desc" components={{ strong: <strong /> }} />
                  </div>
                </div>
              </div>

              <div className="step">
                <div className="step-spine">
                  <div className="step-num">4</div>
                </div>
                <div className="step-body">
                  <div className="step-title">{t('landing.step4Title')}</div>
                  <div className="step-desc">
                    {t('landing.step4Desc')}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="price-section section-with-border" aria-label={t('landing.pricingAria')}>
            <div className="price-heading">
              <span>{t('landing.pricingTitle')}</span>
              <span className="price-heading-accent">{t('landing.allInclusive')}</span>
            </div>
            <div className="price-block">
              <div className="price-inner">
                {loading && hasRef ? (
                  <div className="price-loading">{t('landing.checkingOffer')}</div>
                ) : (
                  <>
                    {showStrikethrough && (
                      <div className="price-strike" aria-hidden="true">
                        €{DEFAULT_PRICE}
                      </div>
                    )}
                    <div className="price-big">
                      <span className="price-eur">€</span>
                      {displayPrice}
                    </div>
                    <div className="price-note">
                      {t('landing.perPerson')}
                    </div>
                    <div className="price-upfront-note">{t('landing.noUpfront')}</div>
                    <div className="success-fee">
                      <div className="sf-dot" aria-hidden="true" />
                      {t('landing.successFee')}
                    </div>

                    <div className="price-includes-title">{t('landing.whatIncludes')}</div>
                    <div className="price-list">
                      <div className="pl-row">
                        <div className="pl-tick">✓</div>{t('landing.priceList1')}
                      </div>
                      <div className="pl-row">
                        <div className="pl-tick">✓</div>{t('landing.priceList2')}
                      </div>
                      <div className="pl-row">
                        <div className="pl-tick">✓</div>{t('landing.priceList3')}
                      </div>
                    </div>

                  </>
                )}
              </div>

              <div className="price-cta-wrap">
                <div className="price-cta-title">
                  {t('landing.ctaBoxTitle')}
                </div>
                <div className="price-cta-desc">
                  {t('landing.ctaBoxDesc')}
                </div>
                <button type="button" className="price-cta" onClick={handleStart}>
                  <span>{t('landing.ctaBoxButton')}</span>
                </button>
                <p className="price-cta-subnote">{t('landing.ctaFreeNote')}</p>
                <div className="price-legal">
                  {t('landing.ctaLegal')}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="site-footer-top">
            <p className="site-footer-company">
              {t('landing.footer.companyLine1')}{' '}
              <span>{t('landing.footer.companyName')}</span>, {t('landing.footer.companyAddress')}.{' '}
              {t('landing.footer.companyLine2')}{' '}
              <a href="mailto:legal@globalmobility.law">legal@globalmobility.law</a>
            </p>

            <div className="site-footer-links">
              <a href="https://globalmobility.law/terminos-servicio">{t('landing.footer.linkTerms')}</a>
              <span aria-hidden="true">·</span>
              <a href="https://globalmobility.law/politica-privacidad">{t('landing.footer.linkPrivacy')}</a>
              <span aria-hidden="true">·</span>
              <a href="https://globalmobility.law/politica-cookies">{t('landing.footer.linkCookies')}</a>
              <span aria-hidden="true">·</span>
              <a href="https://globalmobility.law/aviso-legal">{t('landing.footer.linkLegal')}</a>
            </div>

            <p className="site-footer-copy">{t('landing.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
